'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { searchRooms } from '@/utils/api';
import {
  advanceAddressFocus,
  assignInputRef,
} from '@/components/ui/addressSearchFocus';
import styles from './addressSearchInput.module.css';

export default function RoomSearchInput({
  value = '',
  onChange,
  onRoomSelect = null,
  buildingCode = null, // Required: building code to filter rooms
  placeholder = 'Įveskite buto numerį',
  className = '',
  error = false,
  displayValue = null,
  onEnterPress = null, // Optional callback when Enter key is pressed
  nextInputRef = null,
  inputRef: externalInputRef = null,
}) {
  const [searchTerm, setSearchTerm] = useState(displayValue || value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const prevBuildingCodeRef = useRef(buildingCode);
  const resultsRef = useRef([]);
  resultsRef.current = results;

  // Format room display name
  const formatRoomName = room => {
    return room.room_number || '';
  };

  // Initialize search term from displayValue or value
  useEffect(() => {
    if (displayValue !== null && displayValue !== undefined) {
      setSearchTerm(displayValue);
    } else if (value && !displayValue) {
      setSearchTerm(value);
    }
  }, [value, displayValue]);

  // Reset when building changes
  useEffect(() => {
    const prevBuildingCode = prevBuildingCodeRef.current;
    prevBuildingCodeRef.current = buildingCode;

    // Only reset if buildingCode changed from a value to null/undefined
    if (!buildingCode && prevBuildingCode) {
      setSearchTerm('');
      setSelectedRoom(null);
      setResults([]);
      setIsOpen(false);
      if (value) {
        onChange('');
      }
    }
  }, [buildingCode, value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search function
  const performSearch = useCallback(
    async term => {
      if (!buildingCode) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const rooms = await searchRooms(buildingCode, term || null);
        setResults(rooms || []);
        setIsOpen(true);
        setHighlightedIndex(-1); // Reset highlight when new results arrive
      } catch (error) {
        console.error('[RoomSearchInput] Search error:', error);
        setResults([]);
        setIsOpen(false);
        setHighlightedIndex(-1);
      } finally {
        setIsLoading(false);
      }
    },
    [buildingCode]
  );

  // Handle input change with debounce
  const handleInputChange = e => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If input is cleared, reset everything
    if (!newValue || newValue.trim() === '') {
      setSelectedRoom(null);
      setResults([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      onChange('');
      return;
    }

    // Only search if we have a building code
    if (!buildingCode) {
      return;
    }

    // Debounce search
    debounceTimerRef.current = setTimeout(() => {
      performSearch(newValue);
    }, 300);
  };

  // Confirm exact match or top result on blur when user did not click a suggestion
  const confirmTypedValueIfMatch = useCallback(() => {
    const trimmed = searchTerm?.trim();
    if (!trimmed || selectedRoom || isLoading || !buildingCode) return;

    const currentResults = resultsRef.current;
    if (!currentResults.length) return;

    const roomToSelect =
      currentResults.find(
        r => formatRoomName(r).trim().toLowerCase() === trimmed.toLowerCase()
      ) || currentResults[0];

    setSelectedRoom(roomToSelect);
    setSearchTerm(formatRoomName(roomToSelect));
    setIsOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
    onChange(roomToSelect.code);
    if (onRoomSelect) onRoomSelect(roomToSelect);
  }, [
    searchTerm,
    selectedRoom,
    isLoading,
    buildingCode,
    onChange,
    onRoomSelect,
  ]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && wrapperRef.current.contains(event.target)) {
        return;
      }
      confirmTypedValueIfMatch();
      setIsOpen(false);
      if (selectedRoom) {
        setSearchTerm(formatRoomName(selectedRoom));
      } else if (!value && !searchTerm) {
        setSearchTerm('');
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [selectedRoom, value, searchTerm, confirmTypedValueIfMatch]);

  // Handle input focus
  const handleFocus = () => {
    if (!buildingCode) return;

    // If we have results, show them
    if (results.length > 0) {
      setIsOpen(true);
    }
    // If we have a search term but no results yet, trigger search
    else if (searchTerm && searchTerm.trim().length > 0) {
      performSearch(searchTerm);
    }
    // If no search term, show all rooms
    else {
      performSearch(null);
    }
  };

  // Handle room selection
  const handleSelectRoom = room => {
    setSelectedRoom(room);
    // Use formatted room number
    setSearchTerm(formatRoomName(room));
    setIsOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
    // Pass the room code to the parent
    onChange(room.code);
    // Also call onRoomSelect if provided
    if (onRoomSelect) {
      onRoomSelect(room);
    }
    advanceAddressFocus({ nextInputRef, onEnterPress });
  };

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div
        className={`${styles.inputWrapper} ${error ? styles.inputError : ''} ${isLoading ? styles.loading : ''} ${className}`}
      >
        <input
          ref={node => assignInputRef(inputRef, externalInputRef, node)}
          type='text'
          className={styles.input}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={() => setTimeout(confirmTypedValueIfMatch, 0)}
          onKeyDown={e => {
            if (e.key === 'Enter' && buildingCode) {
              e.preventDefault();
              if (isOpen && results.length > 0) {
                const indexToSelect =
                  highlightedIndex >= 0 ? highlightedIndex : 0;
                const roomToSelect = results[indexToSelect];
                if (roomToSelect) {
                  handleSelectRoom(roomToSelect);
                }
              } else if (!isOpen) {
                advanceAddressFocus({ nextInputRef, onEnterPress });
              }
            } else if (e.key === 'ArrowDown' && isOpen && results.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev =>
                prev < results.length - 1 ? prev + 1 : prev
              );
            }
            // Arrow up - navigate up in results
            else if (e.key === 'ArrowUp' && isOpen && results.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
            }
            // Escape - close dropdown
            else if (e.key === 'Escape' && isOpen) {
              e.preventDefault();
              setIsOpen(false);
              setHighlightedIndex(-1);
            }
          }}
          placeholder={
            buildingCode ? placeholder : 'Pirmiausia pasirinkite namo numerį'
          }
          autoComplete='off'
          disabled={!buildingCode}
        />
        {isLoading && (
          <div className={styles.iconWrapper}>
            <div className={styles.spinner}></div>
          </div>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <ul className={styles.resultsList}>
          {results.map((room, index) => (
            <li
              key={room.code || index}
              className={`${styles.resultItem} ${
                selectedRoom?.code === room.code ? styles.active : ''
              } ${highlightedIndex === index ? styles.highlighted : ''}`}
              onMouseDown={e => {
                e.preventDefault();
                handleSelectRoom(room);
              }}
              onClick={() => handleSelectRoom(room)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className={styles.resultName}>{formatRoomName(room)}</div>
            </li>
          ))}
        </ul>
      )}

      {isOpen &&
        !isLoading &&
        searchTerm.trim().length > 0 &&
        results.length === 0 &&
        buildingCode && (
          <ul className={styles.resultsList}>
            <li className={styles.noResults}>
              Nerasta rezultatų &ldquo;{searchTerm}&rdquo;
            </li>
          </ul>
        )}
    </div>
  );
}
